import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Grid,
  Button,
  Menu,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ShareIcon from "@mui/icons-material/Share";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X"; // For Twitter (X)
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useParams } from "react-router-dom";

/**
 * CareerPage is a React functional component that renders a page displaying
 * job listings, allowing users to search, filter, and apply for jobs.
 *
 * @returns {React.Element} The CareerPage component
 */
export default function CareerPage() {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [page, setPage] = useState(1);
  const [emailOpen, setEmailOpen] = useState(false);
  const [friendEmails, setFriendEmails] = useState("");
  const [yourName, setYourName] = useState("");
  const [yourEmail, setYourEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [dialog, setDialog] = useState({
      open: false,
      title: "",
      message: "",
    });
  const navigate = useNavigate();

  const { headerConfig } = useParams();

  /**
   * Resets the page number to 1 whenever the search, typeFilter, or
   * locationFilter state variables change.
   */
  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, locationFilter]);

  /**
   * Retrieves the job listings from the API when the component mounts.
   *
   * @return {Promise<void>} A Promise that resolves when the job listings are fetched.
   */
  useEffect(() => {
    api.get("/career/jobs/")
      .then((res) => setJobs(res.data));
  }, []);

  /**
   * Generates a list of unique job types from the fetched job listings.
   *
   * @type {Array<string>}
   */
  const jobTypes = [...new Set(jobs.map(j => j.job_type).filter(Boolean))];

  /**
   * Generates a list of unique job locations from the fetched job listings.
   *
   * @type {Array<string>}
   */
  const jobLocations = [...new Set(jobs.map(j => j.job_location).filter(Boolean))];

  /**
   * Filters the job listings based on the search, typeFilter, and locationFilter
   * state variables and returns the filtered list.
   *
   * @returns {Array<Object>} The filtered job listings.
   */
  const filteredJobs = jobs.filter((j) => {
    const matchesSearch =
      (j.job_title ?? "").toLowerCase().includes(search.toLowerCase());

    const matchesType =
      !typeFilter || j.job_type === typeFilter;

    const matchesLocation =
      !locationFilter || j.job_location === locationFilter;

    return matchesSearch && matchesType && matchesLocation;
  });

  /**
   * Calculates the indices of the first and last job to be displayed on the
   * current page.
   *
   * @type {number}
   */
  const indexOfLastJob = page * api.jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - api.jobsPerPage;

  /**
   * Retrieves the job listings to be displayed on the current page based on
   * the filteredJobs list.
   *
   * @type {Array<Object>}
   */
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  /**
   * Calculates the total number of pages based on the filteredJobs list.
   *
   * @type {number}
   */
  const totalPages = Math.ceil(filteredJobs.length / api.jobsPerPage);

  /**
   * Handles the click event on the share button for a job listing.
   *
   * @param {Object} event - The click event object.
   * @param {Object} job - The job listing object.
   * @return {void}
   */
  const handleShareClick = (event, job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  /**
   * Handles the close event for the share menu.
   *
   * @return {void}
   */
  const handleClose = () => {
    setAnchorEl(null);
    setSelectedJob(null);
  };

  /**
   * Handles the sharing of a job on a specific platform.
   *
   * @param {string} platform - The platform to share the job on.
   * @return {void}
   */
  const handlePlatformShare = (platform) => {
    if (!selectedJob) return;

    const jobUrl = `${api.backend_link}job/${selectedJob.public_token}`;
    const text = selectedJob.job_title;

    let shareUrl = "";

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`;
        break;

      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`;
        break;

      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(jobUrl)}`;
        break;

      case "instagram":
        navigator.clipboard.writeText(jobUrl);

        window.open(
          "https://www.instagram.com/",
          "_blank"
        );

        setDialog({
          open: true,
          title: "",
          message: "Job link copied! Paste it in your Instagram post or bio.",
        })
        break;

      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          text + " - " + jobUrl
        )}`;
        break;

      default:
        return;
    }

    const width = 600;
    const height = 600;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    window.open(
      shareUrl,
      "ShareWindow",
      `width=${width},height=${height},top=${top},left=${left}`
    );

    handleClose();
  };

  /**
   * Copies the job link to the clipboard and displays a dialog to indicate the success.
   *
   * @return {void}
   */
  const handleCopyLink = () => {
    if (!selectedJob) return;

    const link = `${api.career_link}apply/${selectedJob.jd_id}`;
    navigator.clipboard.writeText(link);

    setDialog({
      open: true,
      title: "",
      message: "Job link copied!",
    });

    handleClose();
  };

  /**
   * Sends an email to friends with the selected job link.
   *
   * @return {Promise<void>} A Promise that resolves when the email is sent successfully.
   * @throws {Error} If any mandatory fields are missing or email sending fails.
   */
  const handleSendEmail = async () => {
    if (!selectedJob) return;

    if (!friendEmails || !yourName || !yourEmail) {
      setDialogError(
        <span style={{ color: "#ff4d4d" }}>
          Please fill all the mandatory fields. indicated *
        </span>
      );
      return;
    }

    setLoading(true);
    setDialogError("");
    setSuccessMessage("");

    try {
      await api.post("/email-share-job/", {
        job_id: selectedJob.jd_id,
        friend_emails: friendEmails,
        your_name: yourName,
        your_email: yourEmail,
      });

      setSuccessMessage("Email sent successfully");

      setFriendEmails("");
      setYourName("");
      setYourEmail("");

    } catch (error) {
      setDialogError(
        <span style={{ color: "#ff4d4d" }}>
          Mail sending failed
        </span>
      );
    } finally {
      setLoading(false);
      setEmailOpen(false);
    }
  };

  let showHeader = true;

  if (headerConfig?.startsWith("h")) {
    api.header_required=false
    showHeader = headerConfig.endsWith("true");
  }

  return (
    <>
    {api.header_required && (
      <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: api.header_required ? "130px" : "0px",
              backgroundImage:
                "url('https://tse4.mm.bing.net/th/id/OIP.tlagmiTgfXWDiUnZiKjlaAHaCg?rs=1&pid=ImgDetMain&o=7&rm=3')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0.7), rgba(0,0,0,0.4))",
              }}
            />
  
            <Container sx={{ position: "relative", zIndex: 2 }}>
              <Typography variant="h4" fontWeight={800} color="#fff">
                ITConnectUS
              </Typography>
  
              <Typography
                sx={{
                  mt: 1,
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-block",
                  borderBottom: "2px solid #fff",
                }}
                onClick={() => navigate("/")}
              >
                HOME
              </Typography>
            </Container>
          </Box>
        </Box>
    )}

      <Container maxWidth="xl">

        <Box
            sx={{
              position: "sticky",
              top: api.header_required ? 130 : 0,
              zIndex: 1000,
              display: "flex",
              gap: 3,
              mb: 6,
              flexWrap: "wrap",
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              background: "#fff",
            }}
          >
          <TextField
            label="Search Job"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Job Type</InputLabel>
            <Select
              value={typeFilter}
              label="Job Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {jobTypes.map((type, i) => (
                <MenuItem key={i} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={locationFilter}
              label="Location"
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {jobLocations.map((loc, i) => (
                <MenuItem key={i} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {filteredJobs.length === 0 ? (
          <Typography color="error" textAlign="center">
            No jobs found
          </Typography>
        ) : (
          <>
            <Box>
              {currentJobs.map((job) => (
                <Box
                    key={job.jd_id}
                    sx={{
                      p: 4,
                      mb: 1,
                      borderRadius: 2,
                      background: "#f9f9f9df",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      transition: "all 0.3s ease",
                      cursor: "pointer",

                      "&:hover": {
                        background: "#ffffff",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                  <Box>
                    <Typography
                      sx={{
                          fontSize: 22,
                          fontWeight: 600,
                          color: "#0088cc",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => navigate(`/apply/${job.jd_id}`)}
                      >
                      {job.job_title}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
                    {job?.job_location &&
                      !job.job_location.toLowerCase().includes("not specified") && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 18, color: "#383bf2" }} />
                          <Typography>{job.job_location}</Typography>
                        </Box>
                      )}

                      {job?.job_type &&
                      !job.job_type.toLowerCase().includes("not specified") && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <WorkIcon sx={{ fontSize: 18, color: "#383bf2" }} />
                          <Typography>{job.job_type}</Typography>
                        </Box>
                      )}

                      <Typography color="text.secondary">
                        Job ID: {job.jd_display_id}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, md: 0 } }}>
                    <Button
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={(e) => handleShareClick(e, job)}
                      sx={{
                        color: "#333",
                        borderColor: "#ccc",
                      }}
                    >
                      SHARE
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => navigate(`/apply/${job.jd_id}`)}
                    >
                      APPLY
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>

            <Typography sx={{ textAlign: "center", mt: 3 }}>
              Showing {indexOfFirstJob + 1}–
              {Math.min(indexOfLastJob, filteredJobs.length)} of{" "}
              {filteredJobs.length} jobs
            </Typography>

            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
      </Container>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >

        <MenuItem onClick={() => handlePlatformShare("facebook")}>
          <FacebookIcon sx={{ mr: 1, color: "#1877F2" }} />
          Post on Facebook
        </MenuItem>

        <MenuItem onClick={() => handlePlatformShare("linkedin")}>
          <LinkedInIcon sx={{ mr: 1, color: "#0A66C2" }} />
          Post on LinkedIn
        </MenuItem>

        <MenuItem onClick={() => handlePlatformShare("twitter")}>
          <XIcon sx={{ mr: 1 }} />
          Post on X
        </MenuItem>

        <MenuItem onClick={() => handlePlatformShare("instagram")}>
          <InstagramIcon sx={{ mr: 1, color: "#E4405F" }} />
          Post on Instagram
        </MenuItem>

        <MenuItem onClick={() => handlePlatformShare("whatsapp")}>
          <WhatsAppIcon sx={{ mr: 1, color: "#25D366" }} />
          Share on WhatsApp
        </MenuItem>

        <MenuItem onClick={() => setEmailOpen(true)}>
          <EmailIcon sx={{ mr: 1, color: "#1976d2" }} />
          Email to Friend
        </MenuItem>

        <MenuItem onClick={handleCopyLink}>
          <ContentCopyIcon sx={{ mr: 1 }} />
          Copy Link
        </MenuItem>

      </Menu>

      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Send this job to your friends</DialogTitle>

      <DialogContent>
        <TextField
          label={
              <span style={{ fontSize: "13px", color: "#000" }}>
                  Friends' Email IDs (multiple emails separated by commas) <span style={{color: "#ff4d4d"}}>*</span>
              </span>
              }
          fullWidth
          margin="normal"
          value={friendEmails}
          onChange={(e) => setFriendEmails(e.target.value)}
        />

        <TextField
          label={
              <span style={{ fontSize: "13px", color: "#000" }}>
                  Your Full Name <span style={{color: "#ff4d4d"}}>*</span>
              </span>
              }
          fullWidth
          margin="normal"
          value={yourName}
          onChange={(e) => setYourName(e.target.value)}
        />

        <TextField
        label={
              <span style={{ fontSize: "13px", color: "#000" }}>
                  Your Email <span style={{color: "#ff4d4d"}}>*</span>
              </span>
              }
          fullWidth
          margin="normal"
          value={yourEmail}
          onChange={(e) => setYourEmail(e.target.value)}
        />

        <div style={{ width: "100%" }}>
            {successMessage && (
                <Typography sx={{ color: "green", mt: 2 }}>
                  {successMessage}
                </Typography>
              )}

              {dialogError && (
                <Typography sx={{ mt: 2 }}>
                  {dialogError}
                </Typography>
              )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" sx={{ backgroundColor: "#1976d2", textTransform: "none" }} onClick={() => setEmailOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSendEmail}
          sx={{ backgroundColor: "#1976d2", textTransform: "none" }}
        >
          Send
        </Button>
        {loading && (
            <Backdrop
              open
              sx={{
                position: "absolute",
                borderRadius: 2,
                zIndex: 10,
              }}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
      </DialogActions>
    </Dialog>
    <Dialog
        open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography fontSize="14px">
            {dialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDialog({ ...dialog, open: false })}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box textAlign="center" py={6} sx={{ opacity: 0.6 }}>
        <Typography variant="body2">
          © {new Date().getFullYear()} {api.app_name}. Built for the future of hiring.
        </Typography>
      </Box>
    </>
  );
}